import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Link } from "react-router";
import { resolveDocLink } from "../lib/docs-registry";

/**
 * Renders a single markdown doc with the ANANSI brand. GFM tables/strikethrough
 * are enabled (the guides lean on tables heavily). Internal links to other
 * hosted guides become in-app `<Link>`s; everything else (runbooks, specs,
 * external URLs) renders as a plain anchor that opens in a new tab.
 */
export function DocView({ content, sourcePath }: { content: string; sourcePath: string }) {
  return (
    <div className="markdown">
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          a({ href, children, ...rest }) {
            // Another hosted guide → in-app navigation.
            const target = href ? resolveDocLink(href, sourcePath) : null;
            if (target) return <Link to={target}>{children}</Link>;
            // In-page anchor or external URL → real anchor (external opens a tab).
            const isAnchor = href?.startsWith("#") ?? false;
            const isExternal = href ? /^[a-z]+:/i.test(href) : false;
            if (isAnchor || isExternal) {
              return (
                <a href={href} {...(isExternal ? { target: "_blank", rel: "noreferrer noopener" } : {})} {...rest}>
                  {children}
                </a>
              );
            }
            // A relative link to something NOT hosted here (a runbook, a spec).
            // Render as non-navigable text rather than a link that 404s inside
            // the SPA; the path is kept in the title for reference.
            return (
              <span className="markdown__offsite" title={href}>
                {children}
              </span>
            );
          },
        }}
      >
        {content}
      </Markdown>
    </div>
  );
}
